import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getBranches } from '@/api/lookups.api';
import { createUser, updateUser } from '@/api/users.api';
import { ApiError } from '@/api/errors';
import { FormField } from '@/components/form/FormField';
import { lookupLabel } from '@/lib/lookups';
import { showErrorToast } from '@/lib/toast';
import type { LookupItem, Role, UserResponse } from '@/types/shared';
import './UserFormPanel.css';

interface UserFormPanelProps {
  mode: 'create' | 'edit';
  /** Required in edit mode — the user being edited. */
  user?: UserResponse;
  roles: Role[];
  /** The signed-in Admin's own id — used to disable the "Active" checkbox when editing their own
   * account (the server rejects `isActive:false` on yourself with `CANNOT_DEACTIVATE_SELF`
   * anyway; disabling it here just avoids the round-trip). */
  currentUserId?: string;
  onCancel: () => void;
  onSuccess: (user: UserResponse, message: string) => void;
}

/**
 * T174 (§20, `docs/design/19-branch-scoping-ux.md` §2.1). "All branches" and "every branch
 * individually ticked" are two different MODES, not two states of the same checklist — `null` is a
 * third, deliberate state ("not yet chosen") that only exists on create, or right after switching
 * the Role field away from Admin (§2.4: never silently inherit the previous role's selection).
 */
type BranchAccessMode = 'all' | 'specific' | null;

interface FormState {
  fullName: string;
  email: string;
  password: string;
  role: Role | '';
  isActive: boolean;
  branchAccessMode: BranchAccessMode;
  /** Meaningful only when `branchAccessMode === 'specific'`. */
  branchIds: number[];
}

interface FieldErrors {
  fullName?: string;
  email?: string;
  password?: string;
  role?: string;
  branches?: string;
}

function initialBranchAccessMode(user?: UserResponse): BranchAccessMode {
  if (!user || user.role === 'Admin') return null;
  return user.allBranches ? 'all' : 'specific';
}

/**
 * Create/edit form for a staff user, rendered inline (slide-down panel) above the table by
 * `UsersPage` — mirrors the Lookups management screen's add-form convention. `email` is rendered
 * read-only in edit mode (create-only/immutable per the API contract, D-U3) and the initial
 * password field only appears on create (D-U2 — Admin sets it directly; changing it later is the
 * separate "Reset password" row action / `ResetPasswordDialog`).
 *
 * T174 (§20, DEC-BR2/DEC-BR5, `docs/design/19-branch-scoping-ux.md` §2) adds the branch-assignment
 * control directly after the Role field: a mode switch ("All branches" / "Specific branches") plus
 * a conditional checklist, or — when Role is Admin — a fixed, read-only notice (Admins always
 * resolve to all-branches, DEC-BR5/FR-12, and the write path forces it regardless of what's sent).
 */
export function UserFormPanel({
  mode,
  user,
  roles,
  currentUserId,
  onCancel,
  onSuccess,
}: UserFormPanelProps) {
  const { t, i18n } = useTranslation();
  const isEditingSelf = mode === 'edit' && user?.id === currentUserId;

  const [form, setForm] = useState<FormState>({
    fullName: user?.fullName ?? '',
    email: user?.email ?? '',
    password: '',
    role: user?.role ?? '',
    isActive: user?.isActive ?? true,
    branchAccessMode: initialBranchAccessMode(user),
    branchIds: user && !user.allBranches ? user.branches.map((b) => b.id) : [],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [branches, setBranches] = useState<LookupItem[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [branchesLoadError, setBranchesLoadError] = useState<string | null>(null);

  // The branches list is needed for both create and edit (the checklist, and — even for an Admin
  // being edited — the list would be needed the moment the Role is changed away from Admin), so it
  // is fetched once regardless of mode. `activeOnly: false` — an inactive branch already assigned to
  // this user (FR-10) must still render, checked, in the checklist (10-design-system.md §4.3
  // "show the stale value, greyed" convention, applied to a checklist).
  useEffect(() => {
    let cancelled = false;
    setBranchesLoading(true);
    setBranchesLoadError(null);
    getBranches({ activeOnly: false })
      .then((data) => {
        if (!cancelled) setBranches(data);
      })
      .catch(() => {
        if (!cancelled) setBranchesLoadError(t('usersSettings.form.branchesLoadError'));
      })
      .finally(() => {
        if (!cancelled) setBranchesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  /** Reactive to the Role select (§2.4): switching TO Admin collapses the block to a fixed notice
   * and discards any in-progress branch selection; switching AWAY from Admin restores the mode
   * switch, defaulting back to unselected (`null`) rather than silently inheriting whatever the
   * previous role's selection happened to be. A non-Admin -> non-Admin change leaves the branch
   * selection untouched. */
  function handleRoleChange(nextRole: Role) {
    setForm((prev) => {
      const wasAdmin = prev.role === 'Admin';
      const isAdmin = nextRole === 'Admin';
      if (isAdmin || (wasAdmin && !isAdmin)) {
        return { ...prev, role: nextRole, branchAccessMode: null, branchIds: [] };
      }
      return { ...prev, role: nextRole };
    });
    setFieldErrors((prev) => ({ ...prev, role: undefined, branches: undefined }));
  }

  function setBranchAccessMode(nextMode: 'all' | 'specific') {
    setForm((prev) => ({
      ...prev,
      branchAccessMode: nextMode,
      // Switching to "All branches" collapses the checklist entirely (§2.2) — any in-progress
      // selection is discarded, never carried over silently.
      branchIds: nextMode === 'all' ? [] : prev.branchIds,
    }));
    setFieldErrors((prev) => ({ ...prev, branches: undefined }));
  }

  function toggleBranch(branchId: number) {
    setForm((prev) => ({
      ...prev,
      branchIds: prev.branchIds.includes(branchId)
        ? prev.branchIds.filter((id) => id !== branchId)
        : [...prev.branchIds, branchId],
    }));
    setFieldErrors((prev) => ({ ...prev, branches: undefined }));
  }

  /** §2.3 — moves focus to the first checkbox on a rejected empty-scope submit (client or server
   * side). When the mode itself was never chosen (no checklist mounted at all), falls back to the
   * first radio, the first focusable control in the block. */
  function focusBranchesError() {
    const firstCheckbox = document.querySelector<HTMLInputElement>('[data-branch-checkbox]');
    if (firstCheckbox) {
      firstCheckbox.focus();
      return;
    }
    document.getElementById('user-branchAccessMode-all')?.focus();
  }

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (!form.fullName.trim()) errors.fullName = t('usersSettings.form.fullNameRequired');
    else if (form.fullName.trim().length < 2)
      errors.fullName = t('usersSettings.form.fullNameTooShort');

    if (mode === 'create') {
      if (!form.email.trim()) errors.email = t('usersSettings.form.emailRequired');
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
        errors.email = t('usersSettings.form.emailInvalid');

      if (!form.password) errors.password = t('usersSettings.form.passwordRequired');
      else if (form.password.length < 8) errors.password = t('usersSettings.form.passwordTooShort');
    }

    if (!form.role) errors.role = t('usersSettings.form.roleRequired');

    // §2.3/DEC-BR11 — unreachable via the UI in the common case: block "Specific branches" with
    // zero checked, and the never-chosen `null` state, before it ever reaches the server.
    if (form.role && form.role !== 'Admin') {
      if (form.branchAccessMode === null || form.branchAccessMode === 'specific') {
        if (form.branchAccessMode === null || form.branchIds.length === 0) {
          errors.branches = t('usersSettings.form.branchAccessEmptyError');
        }
      }
    }

    return errors;
  }

  function applyServerFieldErrors(apiError: ApiError) {
    const errors: FieldErrors = {};
    for (const detail of apiError.details) {
      if (detail.field === 'email') errors.email = t('usersSettings.form.duplicateEmail');
      if (detail.field === 'fullName') errors.fullName = detail.message;
      if (detail.field === 'role') errors.role = detail.message;
      if (detail.field === 'password') errors.password = detail.message;
      // DEC-BR11/BRANCH_SCOPE_EMPTY and DEC-BR5/BRANCH_SCOPE_NOT_APPLICABLE both name `branchIds`
      // as the offending field (users.service.ts `resolveBranchAssignment`) — mapped inline onto
      // the branch checklist, never a generic banner (T174 brief).
      if (detail.field === 'branchIds') {
        errors.branches =
          apiError.code === 'BRANCH_SCOPE_EMPTY'
            ? t('usersSettings.form.branchAccessEmptyError')
            : detail.message;
      }
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...errors }));
      const firstKey = Object.keys(errors)[0];
      if (firstKey === 'branches') focusBranchesError();
      else document.getElementById(`user-${firstKey}`)?.focus();
    } else {
      const message = genericErrorMessage(apiError);
      setBanner(message);
    }
  }

  function genericErrorMessage(apiError: ApiError): string {
    if (apiError.code === 'NETWORK_ERROR') return t('usersSettings.form.networkError');
    if (apiError.code === 'LAST_ACTIVE_ADMIN') return t('usersSettings.form.lastActiveAdminError');
    return apiError.message || t('usersSettings.form.genericError');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBanner(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstKey = Object.keys(errors)[0];
      if (firstKey === 'branches') focusBranchesError();
      else document.getElementById(`user-${firstKey}`)?.focus();
      return;
    }

    const branchAssignment =
      form.role === 'Admin' || form.branchAccessMode === 'all'
        ? { allBranches: true, branchIds: [] as number[] }
        : { allBranches: false, branchIds: form.branchIds };

    setSubmitting(true);
    try {
      if (mode === 'create') {
        const created = await createUser({
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          password: form.password,
          role: form.role as Role,
          isActive: form.isActive,
          ...branchAssignment,
        });
        onSuccess(created, t('usersSettings.form.createSuccess', { name: created.fullName }));
      } else if (user) {
        const updated = await updateUser(user.id, {
          fullName: form.fullName.trim(),
          role: form.role as Role,
          isActive: form.isActive,
          ...branchAssignment,
        });
        onSuccess(updated, t('usersSettings.form.updateSuccess', { name: updated.fullName }));
      }
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      if (apiError.code === 'DUPLICATE_EMAIL') {
        setFieldErrors((prev) => ({ ...prev, email: t('usersSettings.form.duplicateEmail') }));
        document.getElementById('user-email')?.focus();
      } else if (apiError.details.length > 0) {
        applyServerFieldErrors(apiError);
      } else {
        const message = genericErrorMessage(apiError);
        setBanner(message);
        if (apiError.code === 'NETWORK_ERROR') showErrorToast(message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const isAdminRole = form.role === 'Admin';
  // Visible in the checklist: every active branch, plus any inactive branch already assigned to
  // this user (FR-10) — never an inactive branch that isn't currently checked.
  const visibleBranches = branches.filter(
    (branch) => branch.isActive || form.branchIds.includes(branch.id),
  );

  return (
    <form
      className="fa-user-form-panel fa-form-section"
      onSubmit={handleSubmit}
      noValidate
      aria-label={
        mode === 'create' ? t('usersSettings.form.createTitle') : t('usersSettings.form.editTitle')
      }
    >
      <h2 className="fa-form-section__title">
        {mode === 'create'
          ? t('usersSettings.form.createTitle')
          : t('usersSettings.form.editTitle')}
      </h2>

      {banner && (
        <p className="fa-field__error" role="alert">
          {banner}
        </p>
      )}

      <div className="fa-field-row">
        <FormField
          id="user-fullName"
          label={t('usersSettings.form.fullName')}
          required
          error={fieldErrors.fullName}
        >
          <input
            type="text"
            value={form.fullName}
            disabled={submitting}
            onChange={(e) => setField('fullName', e.target.value)}
          />
        </FormField>

        {mode === 'create' ? (
          <FormField
            id="user-email"
            label={t('usersSettings.form.email')}
            required
            error={fieldErrors.email}
          >
            <input
              type="email"
              className="fa-ltr-isolate"
              autoComplete="off"
              value={form.email}
              disabled={submitting}
              onChange={(e) => setField('email', e.target.value)}
            />
          </FormField>
        ) : (
          <div className="fa-field">
            <label htmlFor="user-email">{t('usersSettings.form.email')}</label>
            <input
              id="user-email"
              type="email"
              className="fa-ltr-isolate"
              value={form.email}
              disabled
              readOnly
            />
            <p className="fa-field__hint">{t('usersSettings.form.emailReadOnlyHint')}</p>
          </div>
        )}
      </div>

      <div className="fa-field-row">
        <FormField
          id="user-role"
          label={t('usersSettings.form.role')}
          required
          error={fieldErrors.role}
        >
          <select
            value={form.role}
            disabled={submitting}
            onChange={(e) => handleRoleChange(e.target.value as Role)}
          >
            <option value="" disabled>
              {t('usersSettings.form.rolePlaceholder')}
            </option>
            {roles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </FormField>

        <div className="fa-field">
          <span className="fa-field__label">{t('usersSettings.form.status')}</span>
          <label className="fa-user-form-panel__active-toggle">
            <input
              type="checkbox"
              checked={form.isActive}
              disabled={submitting || isEditingSelf}
              onChange={(e) => setField('isActive', e.target.checked)}
            />
            {t('usersSettings.form.activeLabel')}
          </label>
          {isEditingSelf && (
            <p className="fa-field__hint">{t('usersSettings.form.cannotDeactivateSelfHint')}</p>
          )}
        </div>
      </div>

      {/* T174 — DEC-BR2/DEC-BR5 branch-assignment control (docs/design/19-branch-scoping-ux.md §2).
          A peer concept to Role, placed directly after it, per the spec. */}
      <fieldset className="fa-field fa-user-form-panel__branch-access" disabled={submitting}>
        <legend className="fa-field__label">
          {t('usersSettings.form.branchAccess')}
          {!isAdminRole && (
            <span className="fa-field__required" aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </legend>

        {isAdminRole ? (
          <p className="fa-field__hint">{t('usersSettings.form.branchAccessAdminNotice')}</p>
        ) : (
          <>
            <div className="fa-user-form-panel__branch-mode">
              <div className="fa-user-form-panel__branch-mode-option">
                <label
                  className="fa-user-form-panel__branch-mode-option-row"
                  htmlFor="user-branchAccessMode-all"
                >
                  <input
                    type="radio"
                    id="user-branchAccessMode-all"
                    name="branchAccessMode"
                    aria-describedby="user-branchAccessMode-all-hint"
                    checked={form.branchAccessMode === 'all'}
                    disabled={branchesLoading}
                    onChange={() => setBranchAccessMode('all')}
                  />
                  <span>{t('usersSettings.form.branchAccessAllOption')}</span>
                </label>
                <p id="user-branchAccessMode-all-hint" className="fa-field__hint">
                  {t('usersSettings.form.branchAccessAllHint')}
                </p>
              </div>
              <div className="fa-user-form-panel__branch-mode-option">
                <label
                  className="fa-user-form-panel__branch-mode-option-row"
                  htmlFor="user-branchAccessMode-specific"
                >
                  <input
                    type="radio"
                    id="user-branchAccessMode-specific"
                    name="branchAccessMode"
                    aria-describedby="user-branchAccessMode-specific-hint"
                    checked={form.branchAccessMode === 'specific'}
                    disabled={branchesLoading}
                    onChange={() => setBranchAccessMode('specific')}
                  />
                  <span>{t('usersSettings.form.branchAccessSpecificOption')}</span>
                </label>
                <p id="user-branchAccessMode-specific-hint" className="fa-field__hint">
                  {t('usersSettings.form.branchAccessSpecificHint')}
                </p>
              </div>
            </div>

            {form.branchAccessMode === 'specific' && (
              <fieldset
                className="fa-user-form-panel__branch-checklist"
                aria-describedby={fieldErrors.branches ? 'user-branches-error' : undefined}
              >
                <legend className="fa-visually-hidden">
                  {t('usersSettings.form.branchAccessSpecificOption')}
                </legend>

                {branchesLoadError ? (
                  <p className="fa-field__error">{branchesLoadError}</p>
                ) : branchesLoading ? (
                  <p className="fa-field__hint">{t('usersSettings.form.branchesLoading')}</p>
                ) : (
                  <div className="fa-user-form-panel__branch-grid">
                    {visibleBranches.map((branch) => {
                      const checked = form.branchIds.includes(branch.id);
                      return (
                        <label key={branch.id} className="fa-user-form-panel__branch-checkbox">
                          <input
                            type="checkbox"
                            data-branch-checkbox
                            checked={checked}
                            disabled={!branch.isActive && !checked}
                            onChange={() => toggleBranch(branch.id)}
                          />
                          <bdi>
                            {lookupLabel(branch, i18n.language)}
                            {!branch.isActive && ` ${t('usersSettings.form.branchInactiveSuffix')}`}
                          </bdi>
                        </label>
                      );
                    })}
                  </div>
                )}

                <p
                  className="fa-user-form-panel__branch-count"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {t('usersSettings.form.branchAccessCount', { count: form.branchIds.length })}
                </p>
              </fieldset>
            )}

            {fieldErrors.branches && (
              <p id="user-branches-error" className="fa-field__error" role="alert">
                {fieldErrors.branches}
              </p>
            )}
          </>
        )}
      </fieldset>

      {mode === 'create' && (
        <div className="fa-field">
          <label htmlFor="user-password">
            {t('usersSettings.form.password')}
            <span className="fa-field__required" aria-hidden="true">
              {' '}
              *
            </span>
          </label>
          <div className="fa-user-form-panel__password-row">
            <input
              id="user-password"
              type={showPassword ? 'text' : 'password'}
              className="fa-ltr-isolate"
              autoComplete="new-password"
              value={form.password}
              disabled={submitting}
              aria-invalid={Boolean(fieldErrors.password)}
              aria-describedby={fieldErrors.password ? 'user-password-error' : 'user-password-hint'}
              aria-required="true"
              onChange={(e) => setField('password', e.target.value)}
            />
            <button
              type="button"
              className="fa-user-form-panel__password-toggle"
              aria-pressed={showPassword}
              aria-label={
                showPassword
                  ? t('usersSettings.form.hidePassword')
                  : t('usersSettings.form.showPassword')
              }
              onClick={() => setShowPassword((v) => !v)}
              disabled={submitting}
            >
              {showPassword ? '\u{1F648}' : '\u{1F441}'}
            </button>
          </div>
          {fieldErrors.password ? (
            <p id="user-password-error" className="fa-field__error">
              {fieldErrors.password}
            </p>
          ) : (
            <p id="user-password-hint" className="fa-field__hint">
              {t('usersSettings.form.passwordHint')}
            </p>
          )}
        </div>
      )}

      <div className="fa-form-actions">
        <button
          type="button"
          className="fa-btn fa-btn--secondary"
          disabled={submitting}
          onClick={onCancel}
        >
          {t('usersSettings.form.cancelButton')}
        </button>
        <button type="submit" className="fa-btn fa-btn--primary" disabled={submitting}>
          {submitting ? t('usersSettings.form.savingButton') : t('usersSettings.form.saveButton')}
        </button>
      </div>
    </form>
  );
}
